/**
 * Reasoning Panel — FASE P1 UI
 * ============================
 * Consente al Founder di:
 *  1. Digitare un goal in linguaggio naturale
 *  2. Aggiungere file di contesto (list separata da newline)
 *  3. Lanciare un tick del Cognitive Loop (dry-run di default)
 *  4. Visualizzare il plan generato dal Reasoning Engine + i risultati dell'Executor
 *  5. Applicare per davvero (dry=false) se il piano è convincente
 */
import React, { useState } from "react";

async function api(path, opts = {}) {
    const r = await fetch(path, {
        ...opts,
        headers: { "Content-Type": "application/json", ...(opts.headers || {}) },
    });
    return r.json();
}

function StepBadge({ ok, dryRun }) {
    if (ok && dryRun) return <span style={{ color: "#22d3ee", fontWeight: 600 }}>DRY-OK</span>;
    if (ok) return <span style={{ color: "#22c55e", fontWeight: 600 }}>APPLIED</span>;
    return <span style={{ color: "#f87171", fontWeight: 600 }}>FAILED</span>;
}

export default function ReasoningPanel() {
    const [goal, setGoal] = useState("");
    const [ctx, setCtx] = useState("");
    const [dry, setDry] = useState(true);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    const run = async () => {
        if (!goal.trim()) return;
        setBusy(true);
        setResult(null);
        try {
            const context_files = ctx
                .split(/[\n,]+/)
                .map((s) => s.trim())
                .filter(Boolean);
            const res = await api("/api/cognitive/loop/tick", {
                method: "POST",
                body: JSON.stringify({
                    goal, project_id: "forge",
                    context_files, dry,
                }),
            });
            setResult(res);
        } catch (e) {
            setResult({ ok: false, error: String(e) });
        } finally {
            setBusy(false);
        }
    };

    return (
        <section className="cockpit-panel" data-testid="reasoning-panel">
            <div className="cockpit-panel-title">
                <span className="forge-panel-eye" /> Reasoning + Code Executor
                <span className="forge-panel-sub">
                    FASE P1 · Aria pianifica → Executor applica (permission gate + rollback)
                </span>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: "0 4px" }}>
                <textarea
                    data-testid="reasoning-goal-input"
                    value={goal}
                    onChange={(e) => setGoal(e.target.value)}
                    placeholder='Es. "Aggiungi data-testid={login-submit} al form di /app/frontend/src/pages/Login.jsx"'
                    rows={3}
                    style={{
                        width: "100%", padding: 10, fontSize: 13,
                        background: "rgba(15,18,28,0.6)", color: "#e2e8f0",
                        border: "1px solid rgba(255,255,255,0.10)", borderRadius: 8,
                        fontFamily: "Geist, sans-serif", resize: "vertical",
                    }}
                />
                <textarea
                    data-testid="reasoning-context-input"
                    value={ctx}
                    onChange={(e) => setCtx(e.target.value)}
                    placeholder="File di contesto (uno per riga, path assoluto sotto /app)"
                    rows={2}
                    style={{
                        width: "100%", padding: 10, fontSize: 12,
                        background: "rgba(15,18,28,0.6)", color: "#94a3b8",
                        border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8,
                        fontFamily: "ui-monospace, monospace", resize: "vertical",
                    }}
                />
                <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                    <label style={{ display: "flex", gap: 6, alignItems: "center", color: "#cfd6f4", fontSize: 12 }}>
                        <input
                            type="checkbox"
                            data-testid="reasoning-dry-toggle"
                            checked={dry}
                            onChange={(e) => setDry(e.target.checked)}
                        />
                        Dry-run (nessuna scrittura reale)
                    </label>
                    <button
                        onClick={run}
                        disabled={busy || !goal.trim()}
                        data-testid="reasoning-run-btn"
                        style={{
                            padding: "8px 16px", fontSize: 13, fontWeight: 600,
                            background: busy
                                ? "rgba(139,92,246,0.35)"
                                : "linear-gradient(135deg, #8b5cf6, #22d3ee)",
                            color: "#fff", border: "none", borderRadius: 8,
                            cursor: busy ? "wait" : "pointer",
                        }}
                    >
                        {busy ? "Aria sta ragionando…" : (dry ? "🧪 Simula" : "⚡ Applica ora")}
                    </button>
                    <span style={{ fontSize: 11, color: "#7a83a8" }}>
                        Provider: routing automatico (task=coding) · gate SAFE/LOW_RISK
                    </span>
                </div>
            </div>

            {result && (
                <div
                    data-testid="reasoning-result"
                    style={{ marginTop: 16, padding: 12, background: "rgba(15,18,28,0.55)",
                              borderRadius: 8, border: "1px solid rgba(255,255,255,0.08)" }}
                >
                    <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap",
                                  gap: 8, fontSize: 12 }}>
                        <div>
                            <b style={{ color: result.success ? "#22c55e" : "#f87171" }}>
                                {result.success ? "✓ Tick completato" : "✗ Tick fallito"}
                            </b>
                            <span style={{ color: "#7a83a8", marginLeft: 8 }}>
                                {result.dry_run ? "· dry-run" : "· APPLIED"}
                                {result.provider && ` · ${result.provider}/${result.model}`}
                                {result.duration_ms != null && ` · ${result.duration_ms}ms`}
                            </span>
                        </div>
                    </div>

                    {result.reason_error && (
                        <div style={{ marginTop: 8, color: "#fbbf24", fontSize: 12 }}>
                            <b>Reasoning error:</b> {result.reason_error}
                        </div>
                    )}

                    {result.plan && (
                        <div style={{ marginTop: 10 }}>
                            <div style={{ fontSize: 12, color: "#cfd6f4", marginBottom: 4 }}>
                                <b>Rationale:</b> {result.plan.rationale}
                            </div>
                            <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 8 }}>
                                Risk: <b style={{ color: "#22d3ee" }}>{result.plan.risk_estimate}</b>
                                · Steps: {result.plan.steps?.length ?? 0}
                            </div>
                            <table style={{ width: "100%", fontSize: 11.5, borderCollapse: "collapse" }}>
                                <thead>
                                    <tr style={{ color: "#7a83a8", textAlign: "left" }}>
                                        <th style={{ padding: "4px 6px" }}>#</th>
                                        <th style={{ padding: "4px 6px" }}>Op</th>
                                        <th style={{ padding: "4px 6px" }}>Target</th>
                                        <th style={{ padding: "4px 6px" }}>Risk</th>
                                        <th style={{ padding: "4px 6px" }}>Status</th>
                                        <th style={{ padding: "4px 6px" }}>Note</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(result.execution_results || []).map((er, i) => (
                                        <tr key={i} style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}>
                                            <td style={{ padding: "4px 6px" }}>{er.step_index}</td>
                                            <td style={{ padding: "4px 6px", color: "#22d3ee" }}>{er.op}</td>
                                            <td style={{ padding: "4px 6px", fontFamily: "ui-monospace, monospace" }}>
                                                {(er.path || er.cmd || er.target || "").slice(-42)}
                                            </td>
                                            <td style={{ padding: "4px 6px", color: "#94a3b8" }}>
                                                {er.effective_risk}
                                            </td>
                                            <td style={{ padding: "4px 6px" }}>
                                                <StepBadge ok={er.ok} dryRun={er.dry_run} />
                                            </td>
                                            <td style={{ padding: "4px 6px", color: "#cfd6f4" }}>
                                                {er.error || er.notes || (er.snapshot_id ? `snap ${er.snapshot_id.slice(0,8)}` : "")}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    <details style={{ marginTop: 10 }}>
                        <summary style={{ fontSize: 11, color: "#7a83a8", cursor: "pointer" }}>
                            Raw JSON
                        </summary>
                        <pre style={{ fontSize: 10.5, background: "rgba(0,0,0,0.35)", padding: 8,
                                       borderRadius: 6, overflow: "auto", maxHeight: 280, color: "#94a3b8" }}>
{JSON.stringify(result, null, 2)}
                        </pre>
                    </details>
                </div>
            )}
        </section>
    );
}
